import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Button as ReactstrapButton } from "reactstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Divider,
  Space,
  Form,
  Input,
  Tag,
  Timeline,
  Modal,
  message,
  Alert,
  Spin,
  Popover,
  Tooltip,
  Checkbox,
  Button,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  ScheduleOutlined,
  DownOutlined,
  RightOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { formatCurrency } from "../../utils/Utils";
import RABillApis from "../../api/WorkOrderManagement/RABillApis";
import RABillPdfGenerator from "./RABillPdfGenerator";
import { useSelector } from "react-redux";
import SignatureModal from "../components/SignatureModal";
import ImageUploadCropper from "../components/ImageUploadCropper";
import { Icon } from "../../components/Component";

const { Title, Text } = Typography;
const { TextArea } = Input;

// API Response interface
interface RABillLineAPI {
  raBillLineId: number;
  workOrderLineId: number;
  taskTitle: string;
  contractQuantity: number;
  totalExecutedQty: number;
  totalBilledQty: number;
  billedQuantity: number;
  rate: number;
  lineSubtotal: number;
  gstPercentage: number;
  gstValue: number;
  lineTotal: number;
  workCategoryName: string;
  uomName: string;
}

// Stage line interface
interface StageLine {
  workOrderLineStageId: number;
  stageTitle: string;
  splitPercentage: number;
  rate: number;
  amount: number;
  billedAmount: number;
}

// Additional charge interface
interface AdditionalCharge {
  chargeName: string;
  amount: number;
}

// Mapped interface for table display
interface RABillLine {
  id: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  uom: string;
  contractQuantity: number;
  cumulativeApprovedQuantity: number;
  previousBilledQuantity: number;
  availableQuantity: number;
  thisBillQuantity: number;
  rate: number;
  lineSubtotal: number;
  gstPercentage: number;
  gstValue: number;
  lineTotal: number;
  workCategory: string;
  stageLines?: StageLine[];
}

interface RABillData {
  raBillLines: RABillLineAPI[];
  payments: any[];
  dms: any[];
  raBillId: number;
  raBillSequenceId: string;
  workOrderId: number;
  workOrderNumber: string;
  contractorId: number;
  contractorName: string;
  siteId: number;
  raPeriodFrom: string;
  raPeriodTo: string;
  placeOfSupply: string;
  contractorGstin: string;
  companyGstin: string;
  taxRegime: string;
  // Additional fields can be added as needed
}

const RABillDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "view"; // 'view', 'approval'

  // Get flowType from URL
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [raBill, setRABill] = useState<any>(null);
  const [raBillLines, setRABillLines] = useState<RABillLine[]>([]);
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [deductions, setDeductions] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "return">("approve");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [additionalCharges, setAdditionalCharges] = useState<
    AdditionalCharge[]
  >([]);
  const [riskflagsItems, setRiskflagsItems] = useState<any[]>([]);
  const [advanceItems, setAdvanceItems] = useState<any[]>([]);

  const [currentUserId, setCurrentUserId] = useState<any>(null);
  const [approverIds, setApproverIds] = useState<any>([]);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const [signature, setSignature] = useState({
    isActive: false,
    label: "Authorised Signatory",
    image: null as string | null,
  });

  const [showSignatureModel, setShowSignatureModel] = useState(false);
  const [isModalApproveVisible, setIsModalApproveVisible] = useState(false);
  const [approveRecord, setApproveRecord] = useState<any>(null);
  const [activeSignatureContext, setActiveSignatureContext] = useState<
    "APPROVE" | "REJECT" | null
  >(null);
  const [isStrictMode, setIsStrictMode] = useState<boolean>(false);
  const [isModalRejectedVisible, setIsModalRejectedVisible] = useState(false);
  const [showActionModelVisible, setShowActionModelVisible] = useState(false);
  const [rejectRecord, setRejectRecord] = useState<any>(null);

  useEffect(() => {
    if (currentUser) {
      const { id } = currentUser;
      setCurrentUserId(id);
    }
  }, [currentUser]);

  useEffect(() => {
    if (id) {
      loadRABillDetails();
    }
  }, [id, mode]);

  const loadRABillDetails = async () => {
    if (!id) {
      message.error("RA Bill ID is required");
      return;
    }

    setLoading(true);
    try {
      console.log("Loading RA Bill details for ID:", id);

      // Call the actual API
      const response: any = await RABillApis.getRABillById(id);

      const isStrict =
        response?.data?.isStrictApproval === true ||
        response?.data?.isStrictApproval === 1;
      setIsStrictMode(isStrict);

      if (response.data?.raBillApprovals) {
        const processedApprovals = response.data.raBillApprovals.map((approval: any) => ({
          ...approval,
          level: approval.level?.toLowerCase() || approval.level,
          status: approval.status?.toLowerCase() || approval.status
        }));
        response.data.raBillApprovals = processedApprovals;

        // Filter pending approvals
        let ids = processedApprovals
          ?.filter((item: any) => item?.status?.toLowerCase() !== "approved")
          ?.map((item: any) => item?.userId);
        setApproverIds(ids);
      }


      if (response && response.status && response.data) {
        const apiData: any = response.data;
        console.log("Loaded RA Bill data:", apiData);

        // Map API data to component state
        const mappedRABill = {
          id: apiData.raBillId,
          raBillNumber: apiData.raBillSequenceId,
          workOrderNumber: apiData.workOrderNumber,
          contractor: apiData.contractorName,
          contractorId: apiData.contractorId,
          siteId: apiData.siteId,
          site: apiData.siteName,
          raPeriod: {
            from: apiData.raPeriodFrom,
            to: apiData.raPeriodTo,
          },
          placeOfSupply: apiData.placeOfSupply,
          contractorGstin: apiData.contractorGstin,
          companyGstin: apiData.companyGstin,
          taxRegime: apiData.taxRegime,
          status: apiData.status,

          // Calculate commercial summary from lines data
          woTotalValue: apiData.workOrderValue,
          cumulativeWorkValue: apiData.totalExecutedValue,
          currentBillValue: apiData.netPayable,
          previousRABillsValue: apiData.totalBilledValue - apiData.netPayable,
          cumulativeWorkDone: apiData.progressPercent,
          balanceWorksForRABill:
            apiData.workOrderValue - apiData.totalExecutedValue,

          // Payment information (if available in API)
          paymentStatus: apiData.paymentStatus,
          paidAmount: apiData.paidAmount,
          lastPaymentDate: null,
          paymentMode: null,

          // Audit trail (you may need separate API calls for these)
          createdBy: "System",
          createdOn: new Date().toISOString(),
          submittedBy: null,
          submittedOn: null,
          approvedBy: null,
          approvedOn: null,

          // Approval hierarchy
          raBillApprovals: apiData.raBillApprovals || [],
        };

        setRABill(mappedRABill);

        // Map RA Bill lines to match the table structure
        const mappedLines = apiData.raBillLines.map(
          (line: any, index: any) => ({
            id: line.raBillLineId.toString(),
            workLineNumber: `WL-${line.workOrderLineId}`,
            workLineTitle: line.taskTitle,
            description: line.taskTitle, // Use taskTitle as description if no separate description
            uom: line.uomName,
            contractQuantity: line.contractQuantity,
            cumulativeApprovedQuantity: line.totalExecutedQty,
            previousBilledQuantity: Math.max(
              0,
              line.totalBilledQty - line.billedQuantity
            ),
            availableQuantity: line.totalExecutedQty - line.totalBilledQty,
            thisBillQuantity: line.billedQuantity,
            rate: line.rate,
            lineSubtotal: line.lineSubtotal,
            gstPercentage: line.gstPercentage,
            gstValue: line.gstValue,
            lineTotal: line.lineTotal,
            workCategory: line.workCategoryName,
            // Map stageLines if available
            stageLines:
              line.stageLines?.map((stage: any) => ({
                workOrderLineStageId: stage.workOrderLineStageId,
                stageTitle: stage.stageTitle || "",
                splitPercentage: stage.splitPercentage || 0,
                rate: stage.rate || 0,
                amount: stage.allocatedQty || 0,
                stageApprovedAmount: stage.totalBilledQty || 0,
                stagePreviousBilledAmount: stage.stagePreviousBilledQty || 0,
                stagePendingBilledAmount:
                  stage.totalPendingApprovalBilledQty || 0,
                lineSubtotal: stage.billedQuantity || 0,
                lineSubtotalAmt: stage.lineSubtotal || 0,
              })) || [],
          })
        );

        setRABillLines(mappedLines);

        // Map additional charges breakdown
        const mappedAdditionalCharges: AdditionalCharge[] =
          apiData.additionalChargesBreakdown?.map((charge: any) => ({
            chargeName: charge.chargeName || "",
            amount: charge.amount || 0,
          })) || [];
        setAdditionalCharges(mappedAdditionalCharges);

        // Map Riskflags and Advance Items from raBillAdvanceDeductions
        const riskflagsItemsData =
          apiData.raBillAdvanceDeductions?.filter(
            (item: any) => item.type === "Risk Flag"
          ) || [];
        const advanceItemsData =
          apiData.raBillAdvanceDeductions?.filter(
            (item: any) =>
              item.type === "Mobilization Advance" || item.type === "Advance"
          ) || [];
        setRiskflagsItems(riskflagsItemsData);
        setAdvanceItems(advanceItemsData);

        // Map workOrder data for calculations
        const mappedWorkOrder = {
          woTotalValue: apiData.workOrderValue,
          tdsRate: apiData.tdsRate,
          tdsTaxName: apiData.tdsTaxName,
          tcsRate: apiData.tcsRate,
          tcsTaxName: apiData.tcsTaxName,
        };
        setWorkOrder(mappedWorkOrder);

        // Calculate totals from the lines data with detailed breakdown
        const subtotalExclTax = apiData.raBillLines.reduce(
          (sum: any, line: any) => sum + line.lineSubtotal,
          0
        );
        const gstTotal = apiData.raBillLines.reduce(
          (sum: any, line: any) => sum + line.gstValue,
          0
        );
        const grossTotal = subtotalExclTax + gstTotal;

        // Calculate GST slab-wise breakdown
        const gstSlabBreakdown: any = {};
        apiData.raBillLines.forEach((line: any) => {
          if (line.gstPercentage > 0) {
            const rateKey = line.gstPercentage.toString();
            if (!gstSlabBreakdown[rateKey]) {
              gstSlabBreakdown[rateKey] = {
                cgstRate: 0,
                sgstRate: 0,
                igstRate: 0,
                taxAmount: 0,
              };
            }

            // Assuming equal split for CGST/SGST or full IGST
            // You may need to adjust based on actual API response
            if (line.igstValue || apiData.taxRegime === "IGST") {
              gstSlabBreakdown[rateKey].igstRate += line.gstValue;
            } else {
              const halfGst = line.gstValue / 2;
              gstSlabBreakdown[rateKey].cgstRate += halfGst;
              gstSlabBreakdown[rateKey].sgstRate += halfGst;
            }
            gstSlabBreakdown[rateKey].taxAmount += line.gstValue;
          }
        });

        // TCS calculation - on Gross Total
        const tcsAmount =
          apiData.tcsRate && grossTotal > 0
            ? (grossTotal * apiData.tcsRate) / 100
            : 0;

        // TDS calculation - on Subtotal only
        const tdsAmount =
          apiData.tdsRate && subtotalExclTax > 0
            ? (subtotalExclTax * apiData.tdsRate) / 100
            : 0;

        // Map deductions with calculation details
        // For Mobilization, use mobilizationRecoveryPercentage as the recovery
        const mobilizationCondition = apiData.workOrderPaymentConditions.find(
          (condition: any) => condition.conditionType === "Mobilization"
        );
        console.log(
          "MobilizationrecoveryPercentRate",
          apiData.workOrderPaymentConditions,
          mobilizationCondition
        );
        const mappedDeductions = [
          {
            id: "mobilization",
            type: "Mobilization",
            amount: apiData.mobilizationRecovery || 0,
            percentValue: apiData.mobilizationRecoveryPercentage || 0,
            calculatedAmount: apiData.mobilizationRecovery || 0,
            // recoveryPercentRate comes from mobilizationRecoveryPercentage in RA Bill API
            recoveryPercentRate: mobilizationCondition
              ? mobilizationCondition.recoveryPercentRate
              : 0,
            balanceMobilizationRecovery:
              apiData.balanceMobilizationRecovery || 0,
            interestCharged: mobilizationCondition
              ? mobilizationCondition.interestCharged
              : false,
            interestPercentage: mobilizationCondition
              ? mobilizationCondition.interestRate
              : 0,
            interestAmount: apiData.mobilizationInterest,
            interestDays: apiData.interestDays || 0,
            interestFromDate: apiData.interestFromDate || "",
            interestToDate: apiData.interestToDate || "",
          },
          {
            id: "retention",
            type: "Retention",
            amount: apiData.retentionAmount || 0,
            percentValue: apiData.retentionAmountPercentage || 0,
            calculatedAmount: apiData.retentionAmount || 0,
          },
          {
            id: "onhold",
            type: "On Hold",
            amount: apiData.onHoldAmount || 0,
            percentValue: apiData.onHoldAmountPercentage || 0,
            calculatedAmount: apiData.onHoldAmount || 0,
          },
        ].filter((deduction) => deduction.amount > 0);

        setDeductions(mappedDeductions);

        // Calculate payment deductions total (excluding TDS)
        const paymentDeductions = mappedDeductions.reduce(
          (sum, d) => sum + d.amount,
          0
        );
        console.log("paymentDeductions", mappedDeductions);
        // Total deductions = Payment Deductions + TDS
        const totalDeductions = paymentDeductions + tdsAmount;

        // Get mobilization interest details
        const interestCharged = mobilizationCondition
          ? mobilizationCondition.interestCharged
          : false;
        const interestAmount = interestCharged
          ? apiData.mobilizationInterest || 0
          : 0;
        const interestPercentage = mobilizationCondition
          ? mobilizationCondition.interestRate
          : 0;
        const interestDays = apiData.interestDays || 0;
        const interestFromDate = apiData.interestFromDate || "";
        const interestToDate = apiData.interestToDate || "";

        // Get Risk Flag Deductions from API
        const riskFlagDeductions = apiData.riskFlagDeductions || 0;

        // Net Payable = Gross Total + TCS + Interest Amount - Total Deductions - Risk Flag Deductions
        const netPayable =
          grossTotal + tcsAmount + interestAmount - totalDeductions;

        const calculatedTotals = {
          id: `totals-${apiData.raBillId}`,
          raBillId: apiData.raBillId.toString(),
          subtotalExclTax: subtotalExclTax,
          gstTotal: gstTotal,
          grossTotal: grossTotal,
          gstSlabBreakdown: gstSlabBreakdown,
          tcsAmount: tcsAmount,
          tcsRate: apiData.tcsRate || 0,
          tcsTaxName: apiData.tcsTaxName || "",
          tdsAmount: tdsAmount,
          tdsRate: apiData.tdsRate || 0,
          tdsTaxName: apiData.tdsTaxName || "",
          paymentDeductions: paymentDeductions,
          totalDeductions: totalDeductions,
          riskFlagDeductions: riskFlagDeductions,
          // Mobilization Interest fields
          interestCharged: interestCharged,
          interestAmount: interestAmount,
          interestPercentage: interestPercentage,
          interestDays: interestDays,
          interestFromDate: interestFromDate,
          interestToDate: interestToDate,
          netPayable: netPayable,
        };
        console.log("calculatedTotals", calculatedTotals);
        setTotals(calculatedTotals);

        message.success("RA Bill details loaded successfully");
      } else {
        throw new Error(response?.message || "Failed to fetch RA Bill details");
      }
    } catch (error: any) {
      console.error("Error loading RA Bill details:", error);
      message.error(
        error?.response?.data?.message || "Failed to load RA Bill details"
      );

      // Navigate back if RA Bill not found
      if (error?.response?.status === 404) {
        setTimeout(() => {
          handleBackNavigation();
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const parseLevelNum = (level: any) => {
    if (!level && level !== 0) return NaN;
    const str = String(level).toLowerCase();

    // Handle various level formats: level1, Level2, L1, L2, etc.
    if (str.includes('level')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    } else if (str.startsWith('l')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    }
    return NaN;
  };

  const hasUserAlreadyApproved = raBill?.raBillApprovals?.some(
    (a: any) =>
      a.userId === currentUser?.id &&
      a.status?.toUpperCase() === "APPROVED"
  );

  const canUserApproveReject = useCallback(() => {
    // 1. Admin can Approve/Reject anytime - Buttons are always ENABLED
    if (currentUser?.genericRole?.toLowerCase() === "admin") {
      return true;
    }

    const approvals = raBill?.raBillApprovals || [];

    // Find current user's approval entry
    const userApproval = approvals.find(
      (approval: any) => approval.userId === currentUserId
    );

    if (!userApproval || userApproval.status?.toLowerCase() !== "pending") {
      return false;
    }

    const userLevelNum = parseLevelNum(userApproval.level ?? currentUser?.approvalLevel);

    // 2. Strict = 0 (Flexible mode)
    // Anyone in the approval list can approve at any time
    if (isStrictMode === false) {
      // In flexible mode, just check if user is in the approval list and pending
      return true;
    }

    // 3. Strict = 1 (Sequential mode)
    // Must follow the order. Level 2 cannot see buttons until Level 1 is "Approved"
    if (isStrictMode === true) {
      // Get all pending approvals sorted by level
      const pendingApprovals = approvals
        .filter((approval: any) => approval.status?.toLowerCase() === "pending")
        .sort((a: any, b: any) => {
          const aNum = parseLevelNum(a.level);
          const bNum = parseLevelNum(b.level);
          if (isNaN(aNum) || isNaN(bNum)) return 0;
          return aNum - bNum;
        });

      // If there are no pending approvals, user can't approve
      if (pendingApprovals.length === 0) return false;

      // Get the first pending approval (lowest level)
      const firstPending = pendingApprovals[0];
      const firstPendingLevelNum = parseLevelNum(firstPending.level);

      // Check if user is the first pending approver
      return userApproval.userId === firstPending.userId &&
        userLevelNum === firstPendingLevelNum;
    }

    return false;
  }, [currentUser, currentUserId, raBill?.raBillApprovals, isStrictMode]);

  const getPendingStatusMessage = () => {
    const approvals = raBill?.raBillApprovals || [];
    const pendingApprovals = approvals
      .filter((approval: any) => approval.status?.toLowerCase() === "pending")
      .sort((a: any, b: any) => {
        const aNum = parseLevelNum(a.level);
        const bNum = parseLevelNum(b.level);
        if (isNaN(aNum) || isNaN(bNum)) return 0;
        return aNum - bNum;
      });

    if (pendingApprovals?.length > 0) {
      if (!isStrictMode) {
        // Flexible mode - any approver can approve
        const approverNames = pendingApprovals.map((approval: any) => approval.approverName).join(", ");
        return `Status is pending. Any of these approvers can approve: ${approverNames}`;
      } else {
        // Strict mode - must follow sequence
        const nextApprover = pendingApprovals[0];
        const lvl = parseLevelNum(nextApprover.level);
        const levelText = !isNaN(lvl) ? `Level ${lvl}` : nextApprover.roleName || "";
        return `Status is pending. Waiting for ${nextApprover.approverName} (${levelText}) to approve first.`;
      }
    }

    return "Status is pending by other approvers";
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Draft: "default",
      Submitted: "processing",
      "Pending Approval": "processing",
      Checked: "warning",
      Approved: "success",
      Posted: "purple",
      Paid: "green",
      Returned: "error",
    };
    return statusColors[status] || "default";
  };

  const handleBackNavigation = () => {
    const backRoutes: { [key: string]: string } = {
      approval: "ra-bills-pending-approvals",
      view: "ra-bills-view-approved",
    };

    navigate(-1);
  };

  const openActionModal = (type: "approve" | "return") => {
    if (type === "approve") {
      // For approve, show direct confirmation without modal
      handleApproveConfirmation();
    } else {
      // For reject, show modal with comments
      setActionType(type);
      setActionModalVisible(true);
      form.resetFields();
    }
  };

  const handleApproveConfirmation = () => {
    Swal.fire({
      title: "Approve RA Bill?",
      text: `Are you sure you want to approve RA Bill ${raBill?.raBillNumber}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await handleApprove();
      }
    });
  };

  const handleApprove = async (files: File[] = []) => {
    try {
      setLoading(true);

      const raBillId = parseInt(id || "0");
      if (!raBillId) {
        throw new Error("Invalid RA Bill ID");
      }

      const response: any = await RABillApis.approveRABill(raBillId, files);

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "RA Bill Approved!",
          text: `RA Bill ${raBill?.raBillNumber} has been approved successfully`,
          confirmButtonText: "OK",
        }).then(() => {
          handleBackNavigation();
        });
      } else {
        throw new Error(response?.message || "Failed to approve RA Bill");
      }
    } catch (error: any) {
      console.error("Error approving RA Bill:", error);
      message.error(
        error?.response?.data?.message || "Failed to approve RA Bill"
      );
    } finally {
      setLoading(false);
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
    }
  };

  const handleReject = async (id: string, reason: string, files: File[] = []) => {
    try {
      const response: any = await RABillApis.rejectRABill(
        parseInt(id),
        reason,
        files
      );

      if (response && response.status) {
        message.success("RA Bill rejected successfully!");
        setTimeout(() => {
          handleBackNavigation();
        }, 1500);
      } else {
        throw new Error(
          response?.message || "Failed to reject ra bill"
        );
      }
    } catch (error) {
      const err = error as any;
      message.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reject execution batch"
      );
    } finally {
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
      form.resetFields();
    }
  };

  const handleActionSubmit = async (values: any) => {
    try {
      setLoading(true);

      const raBillId = parseInt(id || "0");
      if (!raBillId) {
        throw new Error("Invalid RA Bill ID");
      }

      let response: any;
      if (actionType === "approve") {
        response = await RABillApis.approveRABill(raBillId);

        if (response && response.status) {
          Swal.fire({
            icon: "success",
            title: "RA Bill Approved!",
            text: `RA Bill ${raBill?.raBillNumber} has been approved successfully`,
            confirmButtonText: "OK",
          }).then(() => {
            handleBackNavigation();
          });
        } else {
          throw new Error(response?.message || "Failed to approve RA Bill");
        }
      } else {
        response = await RABillApis.rejectRABill(
          raBillId,
          values.comments || ""
        );

        if (response && response.status) {
          Swal.fire({
            icon: "info",
            title: "RA Bill Rejected",
            text: `RA Bill ${raBill?.raBillNumber} has been rejected and returned to contractor`,
            confirmButtonText: "OK",
          }).then(() => {
            handleBackNavigation();
          });
        } else {
          throw new Error(response?.message || "Failed to reject RA Bill");
        }
      }

      setActionModalVisible(false);
    } catch (error: any) {
      console.error(`Error ${actionType}ing RA Bill:`, error);
      message.error(
        error?.response?.data?.message || `Failed to ${actionType} RA Bill`
      );
    } finally {
      setLoading(false);
    }
  };

  // Work Lines Table Columns
  const workLinesColumns: TableColumnsType<RABillLine> = useMemo(
    () => [
      {
        title: "Work Line #",
        dataIndex: "workLineNumber",
        key: "workLineNumber",
        width: 120,
        fixed: "left",
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: "Description",
        key: "description",
        width: 200,
        render: (_, record) => (
          <div>
            <div className="fw-bold">{record.workLineTitle}</div>
            <div className="text-muted small">{record.description}</div>
          </div>
        ),
      },
      // {
      //   title: "UOM",
      //   dataIndex: "uom",
      //   key: "uom",
      //   width: 80,
      //   align: "center",
      // },
      {
        title: "Contract Qty",
        dataIndex: "contractQuantity",
        key: "contractQuantity",
        width: 120,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold">{qty?.toLocaleString("en-IN")}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: "Cum Approved Qty",
        dataIndex: "cumulativeApprovedQuantity",
        key: "cumulativeApprovedQuantity",
        width: 130,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold">{qty?.toLocaleString("en-IN")}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: "Prev Billed Qty",
        dataIndex: "previousBilledQuantity",
        key: "previousBilledQuantity",
        width: 120,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold">{qty?.toLocaleString("en-IN")}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: "Available to Bill",
        dataIndex: "availableQuantity",
        key: "availableQuantity",
        width: 120,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold">{qty?.toLocaleString("en-IN")}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
      },
      {
        title: mode === "approval" ? "Billed Qty (Submitted)" : "Billed Qty",
        dataIndex: "thisBillQuantity",
        key: "thisBillQuantity",
        width: 130,
        align: "right",
        render: (qty, record) => (
          <div>
            <div className="fw-bold">{qty?.toLocaleString("en-IN")}</div>
            <div className="text-muted small">{record.uom}</div>
          </div>
        ),
        // render: (qty) => (
        //   <Text
        //     strong
        //     style={{ color: mode === "approval" ? "#1890ff" : "#52c41a" }}
        //   >
        //     {qty.toLocaleString("en-IN")}
        //   </Text>
        // ),
      },
      {
        title: "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 100,
        align: "right",
        render: (rate) => `₹${rate.toLocaleString("en-IN")}`,
      },
      {
        title: "Line Subtotal",
        dataIndex: "lineSubtotal",
        key: "lineSubtotal",
        width: 130,
        align: "right",
        render: (amount) => <Text strong>{formatCurrency(amount, "INR")}</Text>,
      },
      {
        title: "GST %",
        dataIndex: "gstPercentage",
        key: "gstPercentage",
        width: 80,
        align: "center",
        render: (pct) => `${pct}%`,
      },
      {
        title: "GST Value",
        dataIndex: "gstValue",
        key: "gstValue",
        width: 120,
        align: "right",
        render: (amount) => (
          <Text type="warning">{formatCurrency(amount, "INR")}</Text>
        ),
      },
      {
        title: "Line Total",
        dataIndex: "lineTotal",
        key: "lineTotal",
        width: 130,
        align: "right",
        render: (amount) => (
          <Text strong style={{ color: "#52c41a" }}>
            {formatCurrency(amount, "INR")}
          </Text>
        ),
      },
    ],
    [mode]
  );

  if (loading && !raBill) {
    return (
      <Content>
        <div style={{ textAlign: "center", marginTop: "20vh" }}>
          <Spin size="large" />
          <div className="mt-3">Loading RA Bill details...</div>
        </div>
      </Content>
    );
  }

  if (!raBill) {
    return (
      <Content>
        <div style={{ textAlign: "center", marginTop: "20vh" }}>
          <Title level={4}>RA Bill not found</Title>
          <Button onClick={handleBackNavigation}>Go Back</Button>
        </div>
      </Content>
    );
  }

  const handleCancel = () => {
    setShowActionModelVisible(false);
    setIsModalRejectedVisible(false);
    form.resetFields();
  };

  const dataURLToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const handleApproval = async (raBillId: string, files: File[] = []) => {
    try {
      setLoading(true);

      const billId = parseInt(raBillId);
      if (!billId) {
        throw new Error("Invalid RA Bill ID");
      }

      const response: any = await RABillApis.approveRABill(billId, files);

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "RA Bill Approved!",
          text: `RA Bill ${raBill?.raBillNumber} has been approved successfully`,
          confirmButtonText: "OK",
        }).then(() => {
          handleBackNavigation();
        });
      } else {
        throw new Error(response?.message || "Failed to approve RA Bill");
      }
    } catch (error: any) {
      console.error("Error approving RA Bill:", error);
      message.error(
        error?.response?.data?.message || "Failed to approve RA Bill"
      );
    } finally {
      setLoading(false);
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
    }
  };

  const openApproveConfirmSwal = () => {
    if (!signature.image) return;

    setIsModalApproveVisible(false);

    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve it!",
      cancelButtonText: "No, keep it",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const file = dataURLToFile(signature.image as string, "signature.png");
        await handleApproval(approveRecord, [file]);
      } else {
        setIsModalApproveVisible(true);
      }
    });
  };

  const openRejectConfirmSwal = async () => {
    try {
      await form.validateFields();

      if (!signature.image) {
        Swal.fire({
          icon: "info",
          title: "Signature Required",
          text: "Please add your signature before rejecting.",
        });
        return;
      }

      setIsModalRejectedVisible(false);

      Swal.fire({
        title: "Confirm Rejection?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Reject it!",
        cancelButtonText: "Cancel",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const values = form.getFieldsValue();
          await handleReject(rejectRecord, values.reason);
        } else {
          setIsModalRejectedVisible(true);
        }
      });
    } catch (e) {
      console.log("Validation failed:", e);
    }
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = raBill.raBillApprovals;
    if (!approvalsData || approvalsData?.length === 0)
      return <Badge color="outline-danger">No</Badge>;
    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status?.toLowerCase() == "approved";
              const isRejected = approval.status?.toLowerCase() == "rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.approverName}
                  <div className="designation">{approval?.approverRole}</div>
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                  {approval.approvalStage && (
                    <div className="text-muted small">
                      <Tag
                        color="blue"
                        style={{ fontSize: "10px" }}
                      >
                        {approval.approvalStage}
                      </Tag>
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>

          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  return (
    <>
      <Content>
        <Head title={`RA Bill Details - ${raBill.raBillNumber}`} />

        <div className="nk-content-inner">
          <div className="nk-content-body">
            {/* Header */}
            <div className="nk-block-head nk-block-head-sm">
              <div className="nk-block-between g-3">
                <div className="nk-block-head-content">
                  <Title level={3} className="nk-block-title">
                    {mode === "approval" ? "Review RA Bill" : "RA Bill Details"} -{" "}
                    {raBill.raBillNumber}
                  </Title>
                  <div className="nk-block-des text-soft">
                    <Space>
                      <span>WO: {raBill.workOrderNumber}</span>
                      <Divider type="vertical" />
                      <span>Contractor: {raBill.contractor}</span>
                      <Divider type="vertical" />
                      <Tag color={getStatusColor(raBill.status)}>
                        {raBill.status && raBill.status == "Submitted"
                          ? "Pending Approval"
                          : raBill.status}
                      </Tag>
                    </Space>
                  </div>
                </div>
                <div className="nk-block-head-content">
                  <Space>
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={handleBackNavigation}
                      style={{ marginRight: 16 }}
                    >
                      Back
                    </Button>
                    {/* Actions Dropdown */}
                    <Dropdown
                      isOpen={actionsDropdownOpen}
                      toggle={() => setActionsDropdownOpen(!actionsDropdownOpen)}
                    >
                      <DropdownToggle
                        color="primary"
                        className="btn-icon"
                        style={{ padding: "8px 12px" }}
                      >
                        <MoreOutlined style={{ fontSize: "18px" }} />
                      </DropdownToggle>
                      <DropdownMenu end>
                        {/* Export PDF Option (Always visible) */}
                        <DropdownItem
                          onClick={() => {
                            setPdfLoading(true);
                            const pdfTrigger = document.querySelector(
                              "[data-pdf-generator-trigger]"
                            ) as HTMLButtonElement;
                            if (pdfTrigger) {
                              pdfTrigger.click();
                            }
                          }}
                          className="text-primary"
                        >
                          <FilePdfOutlined className="me-2" />
                          Export PDF
                        </DropdownItem>

                        {/* Approve/Reject Options (Only in approval mode) */}
                        {mode === "approval" && !hasUserAlreadyApproved && (
                          <>
                            {canUserApproveReject() ? (
                              <>
                                <DropdownItem
                                  onClick={() => {
                                    setApproveRecord(id);
                                    setActiveSignatureContext("APPROVE");
                                    setIsModalApproveVisible(true);
                                  }}
                                  className="text-success"
                                >
                                  <CheckOutlined className="me-2" />
                                  Approve
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() => {
                                    setRejectRecord(id);
                                    setActiveSignatureContext("REJECT");
                                    setIsModalRejectedVisible(true);
                                    form.resetFields();
                                  }}
                                  className="text-danger"
                                >
                                  <CloseOutlined className="me-2" />
                                  Reject
                                </DropdownItem>
                              </>
                            ) : (
                              <>
                                <DropdownItem divider />
                                {/* APPROVE (Disabled) */}
                                <DropdownItem
                                  style={{
                                    cursor: "not-allowed",
                                    opacity: 0.6,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "8px",
                                  }}
                                  className="text-success"
                                  onClick={(e) => e.preventDefault()}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <CheckOutlined />
                                    <span>Approve</span>
                                  </div>

                                  <Popover
                                    content={
                                      <div style={{ padding: "8px", maxWidth: "300px" }}>
                                        <p style={{ margin: 0 }}>{getPendingStatusMessage()}</p>
                                      </div>
                                    }
                                    title="Approval Status"
                                    trigger="hover"
                                  >
                                    <InfoCircleOutlined
                                      style={{
                                        fontSize: "16px",
                                        cursor: "pointer",
                                        color: "#2263b3",
                                      }}
                                    />
                                  </Popover>
                                </DropdownItem>

                                {/* REJECT (Disabled) */}
                                <DropdownItem
                                  style={{
                                    cursor: "not-allowed",
                                    opacity: 0.6,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                  className="text-danger"
                                  onClick={(e) => e.preventDefault()}
                                >
                                  <div
                                    className="d-flex align-items-center"
                                    style={{ gap: "13px" }}
                                  >
                                    <CloseOutlined />
                                    <span>Reject</span>
                                  </div>
                                </DropdownItem>
                              </>
                            )}
                          </>
                        )}
                      </DropdownMenu>
                    </Dropdown>
                  </Space>
                </div>
              </div>
            </div>

            {/* Header Summary */}
            <Card title="RA Bill Header Details" className="mb-4">
              <Row gutter={[16, 12]}>
                <Col xs={24} sm={12} lg={8}>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="text-muted" style={{ minWidth: "120px" }}>
                      RA Bill Number:
                    </span>
                    <span className="">{raBill.raBillNumber}</span>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="text-muted" style={{ minWidth: "120px" }}>
                      Work Order:
                    </span>
                    <span className="">{raBill.workOrderNumber}</span>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="text-muted" style={{ minWidth: "120px" }}>
                      {isClientFlow ? "Client" : "Contractor"}:
                    </span>
                    <span className="">{raBill.contractor}</span>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="text-muted" style={{ minWidth: "120px" }}>
                      RA Period:
                    </span>
                    <span className="">
                      {dayjs(raBill.raPeriod.from).format("DD-MMM")} to{" "}
                      {dayjs(raBill.raPeriod.to).format("DD-MMM-YYYY")}
                    </span>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="text-muted" style={{ minWidth: "120px" }}>
                      Site:
                    </span>
                    <span className="">{raBill.site}</span>
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="text-muted" style={{ minWidth: "120px" }}>
                      Status:
                    </span>
                    {(raBill.status === "Pending Approval" ||
                      raBill.status === "Submitted") &&
                      raBill.raBillApprovals &&
                      raBill.raBillApprovals.length > 0 ? (
                      <Popover
                        content={
                          <Timeline
                            className="approval-timeline"
                            items={raBill.raBillApprovals.map(
                              (approval: any, index: number) => {
                                const isApproved = approval.status === "Approved";
                                const isRejected = approval.status === "Rejected";
                                return {
                                  key: approval.raBillApprovalId || index,
                                  dot: isApproved ? (
                                    <CheckCircleOutlined
                                      style={{ color: "green" }}
                                    />
                                  ) : isRejected ? (
                                    <CloseCircleOutlined
                                      style={{ color: "red" }}
                                    />
                                  ) : undefined,
                                  children: (
                                    <div>
                                      <div>
                                        <strong>
                                          {approval.approverName ||
                                            `User ${approval.userId}`}
                                        </strong>
                                      </div>
                                      <div className="text-muted small">
                                        {approval.approverRole || ""}
                                      </div>
                                      {approval.approvalStage && (
                                        <div className="text-muted small">
                                          <Tag
                                            color="blue"
                                            style={{ fontSize: "10px" }}
                                          >
                                            {approval.approvalStage}
                                          </Tag>
                                        </div>
                                      )}
                                      {isApproved && approval.approvedByName && (
                                        <div className="text-muted small mt-1">
                                          <span className="text-success">
                                            ✓ Approved by:{" "}
                                            {approval.approvedByName}
                                          </span>
                                        </div>
                                      )}
                                      {isRejected && approval.reason && (
                                        <div className="text-muted small mt-1">
                                          <span className="text-danger">
                                            ✗ Reason: {approval.reason}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  ),
                                };
                              }
                            )}
                          />
                        }
                        title={
                          <span style={{ color: "#2263b3" }}>
                            Approval Hierarchy
                          </span>
                        }
                        trigger="hover"
                      >
                        <span
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          <Tag color={getStatusColor(raBill.status)}>
                            {raBill.status === "Submitted"
                              ? "Pending Approval"
                              : raBill.status}
                          </Tag>
                        </span>
                      </Popover>
                    ) : (
                      <Tag color={getStatusColor(raBill.status)}>
                        {raBill.status}
                      </Tag>
                    )}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <div className="d-flex" style={{ gap: "8px" }}>
                    <span className="text-muted" style={{ minWidth: "120px" }}>
                      Approval Required:
                    </span>
                    <span className="data-value w-70">
                      <ApprovalTimelineContent />
                    </span>
                  </div>
                </Col>
              </Row>

              {/* Commercial Summary */}
              <Divider className="mt-3">Commercial Summary</Divider>
              <Row gutter={[24, 16]}>
                <Col xs={12} lg={5}>
                  <div className="text-center p-3 border rounded">
                    <div className="text-muted small">WO Total Value</div>
                    <div className="fw-bold h5 mb-0">
                      {formatCurrency(raBill.woTotalValue, "INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={5}>
                  <div className="text-center p-3 border rounded">
                    <div className="text-muted small">Cumulative Work Done</div>
                    <div className="fw-bold h5 mb-0 text-success">
                      {raBill.cumulativeWorkDone}%
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={5}>
                  <div className="text-center p-3 border rounded">
                    <div className="text-muted small">Cumulative Work Value</div>
                    <div className="fw-bold h5 mb-0">
                      {formatCurrency(raBill.cumulativeWorkValue, "INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={5}>
                  <div className="text-center p-3 border rounded">
                    <div className="text-muted small">Previous RA Bills</div>
                    <div className="fw-bold h5 mb-0 text-info">
                      {formatCurrency(raBill.previousRABillsValue, "INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={4}>
                  <div className="text-center p-3 border rounded">
                    <div className="text-muted small">Current Bill Value</div>
                    <div className="fw-bold h5 mb-0 text-primary">
                      {formatCurrency(raBill.currentBillValue, "INR")}
                    </div>
                  </div>
                </Col>
                {/* <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Balance Works</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(raBill.balanceWorksForRABill, "INR")}
                  </div>
                </div>
              </Col> */}
              </Row>
            </Card>

            {/* Work Lines */}
            <Card title="Work Lines Details" className="mb-4">
              <Table
                columns={workLinesColumns}
                dataSource={raBillLines}
                rowKey="id"
                scroll={{ x: 1600 }}
                pagination={false}
                size="small"
                expandable={{
                  expandedRowRender: (record: RABillLine) => {
                    if (!record.stageLines || record.stageLines.length === 0)
                      return null;

                    const totalPercentage = record.stageLines.reduce(
                      (sum, stage) => sum + (stage.splitPercentage || 0),
                      0
                    );
                    const totalRateAmount = record.stageLines.reduce(
                      (sum, stage) => sum + (stage.rate || 0),
                      0
                    );
                    const totalContractAmount = record.stageLines.reduce(
                      (sum, stage: any) => sum + (stage.amount || 0),
                      0
                    );
                    const totalApprovedAmount = record.stageLines.reduce(
                      (sum, stage: any) => sum + (stage.stageApprovedAmount || 0),
                      0
                    );
                    const totalPrevBilledAmount = record.stageLines.reduce(
                      (sum, stage: any) =>
                        sum + (stage.stagePreviousBilledAmount || 0),
                      0
                    );
                    const totalPendingBilledAmount = record.stageLines.reduce(
                      (sum, stage: any) =>
                        sum + (stage.stagePendingBilledAmount || 0),
                      0
                    );
                    const totalCurrentBilledAmount = record.stageLines.reduce(
                      (sum, stage: any) => sum + (stage.lineSubtotal || 0),
                      0
                    );
                    const totallineSubtotalAmtount = record.stageLines.reduce(
                      (sum, stage: any) => sum + (stage.lineSubtotalAmt || 0),
                      0
                    );

                    return (
                      <div
                        style={{
                          padding: "12px 16px",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <div
                          style={{
                            marginBottom: 8,
                            fontWeight: 600,
                            color: "#1890ff",
                          }}
                        >
                          <ScheduleOutlined style={{ marginRight: 8 }} />
                          Stage Breakdown ({record.stageLines.length} stages)
                        </div>
                        <Table
                          dataSource={record.stageLines.map((stage, idx) => ({
                            ...stage,
                            key: stage.workOrderLineStageId || idx,
                          }))}
                          columns={[
                            {
                              title: "Stage",
                              dataIndex: "stageTitle",
                              key: "stageTitle",
                              width: 250,
                            },
                            {
                              title: "Percentage",
                              dataIndex: "splitPercentage",
                              key: "splitPercentage",
                              width: 120,
                              render: (val: number) => (
                                <Tag color="blue">{val}%</Tag>
                              ),
                            },
                            {
                              title: "Stage Rate",
                              dataIndex: "rate",
                              key: "rate",
                              width: 150,
                              render: (val: number) => (
                                <span style={{ fontWeight: 500 }}>
                                  ₹
                                  {val?.toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              ),
                            },
                            {
                              title: "Qty",
                              dataIndex: "amount",
                              key: "amount",
                              width: 150,
                            },
                            {
                              title: "Stage Approved Qty",
                              dataIndex: "stageApprovedAmount",
                              key: "stageApprovedAmount",
                              width: 150,
                            },
                            {
                              title: "Prev Billed Qty",
                              dataIndex: "stagePreviousBilledAmount",
                              key: "stagePreviousBilledAmount",
                              width: 150,
                            },
                            {
                              title: "Pending Billed Qty",
                              dataIndex: "stagePendingBilledAmount",
                              key: "stagePendingBilledAmount",
                              width: 150,
                            },
                            {
                              title: "Current Billed Qty",
                              dataIndex: "lineSubtotal",
                              key: "lineSubtotal",
                              width: 150,
                            },
                            {
                              title: "Current Billed Amount",
                              dataIndex: "lineSubtotalAmt",
                              key: "lineSubtotalAmt",
                              width: 150,
                              render: (val: number) => (
                                <span style={{ fontWeight: 500 }}>
                                  ₹
                                  {val?.toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              ),
                            },
                          ]}
                          pagination={false}
                          size="small"
                          bordered
                          summary={() => (
                            <Table.Summary fixed>
                              <Table.Summary.Row
                                style={{ backgroundColor: "#e6f7ff" }}
                              >
                                <Table.Summary.Cell index={0}>
                                  <strong>Total</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                  <Tag
                                    color={
                                      totalPercentage === 100 ? "green" : "orange"
                                    }
                                  >
                                    {totalPercentage}%
                                  </Tag>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>
                                  <strong>
                                    ₹
                                    {totalRateAmount.toLocaleString("en-IN", {
                                      maximumFractionDigits: 2,
                                    })}
                                  </strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>
                                  {/* <strong>
                                  ₹
                                  {totalContractAmount.toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })}
                                </strong> */}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4}>
                                  <strong>{totalApprovedAmount}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5}>
                                  <strong>{totalPrevBilledAmount}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6}>
                                  <strong>{totalPendingBilledAmount}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7}>
                                  <strong style={{ color: "#52c41a" }}>
                                    {totalCurrentBilledAmount}
                                  </strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8}>
                                  <strong>
                                    ₹
                                    {totallineSubtotalAmtount.toLocaleString(
                                      "en-IN",
                                      {
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </strong>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </Table.Summary>
                          )}
                        />
                      </div>
                    );
                  },
                  expandIcon: ({ expanded, onExpand, record }: any) => {
                    const hasStageLines =
                      record.stageLines && record.stageLines.length > 0;
                    if (!hasStageLines) return null;

                    return expanded ? (
                      <DownOutlined
                        onClick={(e) => onExpand(record, e)}
                        style={{
                          color: "#722ed1",
                          cursor: "pointer",
                        }}
                      />
                    ) : (
                      <RightOutlined
                        onClick={(e) => onExpand(record, e)}
                        style={{
                          color: "#722ed1",
                          cursor: "pointer",
                        }}
                      />
                    );
                  },
                  rowExpandable: (record: RABillLine) =>
                    !!(record.stageLines && record.stageLines.length > 0),
                }}
              />
            </Card>

            {/* Riskflags Items and Advance Items */}
            {(riskflagsItems.length > 0 || advanceItems.length > 0) && (
              <Card title="Riskflags & Additional Advances" className="mb-4">
                <div className="mt-3">
                  {/* Riskflags Items */}
                  {riskflagsItems.length > 0 && (
                    <div
                      className="p-3 border rounded bg-light mb-3"
                      style={{ width: "100%" }}
                    >
                      <div className="fw-bold text-primary mb-2">Riskflags</div>
                      <Table
                        dataSource={riskflagsItems}
                        rowKey={(record) =>
                          record.raBillAdvanceDeductionId?.toString() ||
                          record.workOrderAdvanceDeductionId?.toString() ||
                          ""
                        }
                        pagination={false}
                        bordered
                        size="small"
                        columns={[
                          {
                            title: "S.No",
                            key: "sno",
                            width: 60,
                            align: "center",
                            render: (_: any, __: any, index: number) => index + 1,
                          },
                          {
                            title: "Name",
                            dataIndex: "name",
                            key: "name",
                            width: 250,
                            render: (name: string) => {
                              if (!name) return "-";
                              const maxLength = 30;
                              const isLong = name.length > maxLength;
                              const displayText = isLong
                                ? name.substring(0, maxLength) + "..."
                                : name;
                              return (
                                <Tooltip
                                  title={isLong ? name : null}
                                  placement="topLeft"
                                >
                                  <div
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      cursor: isLong ? "pointer" : "default",
                                    }}
                                  >
                                    {displayText}
                                  </div>
                                </Tooltip>
                              );
                            },
                          },
                          {
                            title: "Is Chargeable",
                            dataIndex: "isChargable",
                            key: "isChargable",
                            width: 180,
                            align: "center",
                            render: (isChargable: boolean) => (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                }}
                              >
                                <Checkbox
                                  checked={isChargable}
                                  disabled
                                  className="custom-checkbox-white"
                                />
                                <span>Yes</span>
                              </div>
                            ),
                          },
                          {
                            title: "Amount",
                            dataIndex: "amount",
                            key: "amount",
                            width: 200,
                            align: "right",
                            render: (amount: number) =>
                              `₹${(amount || 0).toLocaleString("en-IN", {
                                maximumFractionDigits: 2,
                              })}`,
                          },
                          {
                            title: "Cumulative Recovered Amount",
                            key: "cumulativeRecoveredAmount",
                            width: 250,
                            align: "right",
                            render: (_: any, record: any) => {
                              const totalRecovered =
                                record.totalRecoveredAmount || 0;
                              const totalPending =
                                record.totalPendingRecoveredAmount || 0;
                              const cumulativeAmount =
                                totalRecovered + totalPending;
                              if (cumulativeAmount > 0) {
                                return (
                                  <div style={{ textAlign: "right" }}>
                                    <span style={{ fontWeight: 500 }}>
                                      ₹
                                      {cumulativeAmount.toLocaleString("en-IN", {
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                );
                              }
                              return <span style={{ color: "#999" }}>-</span>;
                            },
                          },
                          {
                            title: "Remarks",
                            dataIndex: "notes",
                            key: "notes",
                            render: (remarks: string) => {
                              if (!remarks) return "-";
                              const maxLength = 30;
                              const isLong = remarks.length > maxLength;
                              const displayText = isLong
                                ? remarks.substring(0, maxLength) + "..."
                                : remarks;
                              return (
                                <Tooltip
                                  title={isLong ? remarks : null}
                                  placement="topLeft"
                                >
                                  <div
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      cursor: isLong ? "pointer" : "default",
                                    }}
                                  >
                                    {displayText}
                                  </div>
                                </Tooltip>
                              );
                            },
                          },
                        ]}
                      />
                    </div>
                  )}

                  {/* Advance Items */}
                  {advanceItems.length > 0 && (
                    <div
                      className="p-3 border rounded bg-light"
                      style={{ width: "100%" }}
                    >
                      <div className="fw-bold text-primary mb-2">
                        Additional Advances
                      </div>
                      <Table
                        dataSource={advanceItems}
                        rowKey={(record) =>
                          record.raBillAdvanceDeductionId?.toString() ||
                          record.workOrderAdvanceDeductionId?.toString() ||
                          ""
                        }
                        pagination={false}
                        bordered
                        size="small"
                        columns={[
                          {
                            title: "S.No",
                            key: "sno",
                            width: 60,
                            align: "center",
                            render: (_: any, __: any, index: number) => index + 1,
                          },
                          {
                            title: "Name",
                            dataIndex: "name",
                            key: "name",
                            width: 250,
                            render: (name: string) => {
                              if (!name) return "-";
                              const maxLength = 30;
                              const isLong = name.length > maxLength;
                              const displayText = isLong
                                ? name.substring(0, maxLength) + "..."
                                : name;
                              return (
                                <Tooltip
                                  title={isLong ? name : null}
                                  placement="topLeft"
                                >
                                  <div
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      cursor: isLong ? "pointer" : "default",
                                    }}
                                  >
                                    {displayText}
                                  </div>
                                </Tooltip>
                              );
                            },
                          },
                          {
                            title: "Is Chargeable",
                            dataIndex: "isChargable",
                            key: "isChargable",
                            width: 180,
                            align: "center",
                            render: (isChargable: boolean) => (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                }}
                              >
                                <Checkbox
                                  checked={isChargable}
                                  disabled
                                  className="custom-checkbox-white"
                                />
                                <span>Yes</span>
                              </div>
                            ),
                          },
                          {
                            title: "Amount",
                            dataIndex: "amount",
                            key: "amount",
                            width: 200,
                            align: "right",
                            render: (amount: number) =>
                              `₹${(amount || 0).toLocaleString("en-IN", {
                                maximumFractionDigits: 2,
                              })}`,
                          },
                          {
                            title: "Cumulative Recovered Amount",
                            key: "cumulativeRecoveredAmount",
                            width: 250,
                            align: "right",
                            render: (_: any, record: any) => {
                              const totalRecovered =
                                record.totalRecoveredAmount || 0;
                              const totalPending =
                                record.totalPendingRecoveredAmount || 0;
                              const cumulativeAmount =
                                totalRecovered + totalPending;
                              if (cumulativeAmount > 0) {
                                return (
                                  <div style={{ textAlign: "right" }}>
                                    <span style={{ fontWeight: 500 }}>
                                      ₹
                                      {cumulativeAmount.toLocaleString("en-IN", {
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                );
                              }
                              return <span style={{ color: "#999" }}>-</span>;
                            },
                          },
                          {
                            title: "Remarks",
                            dataIndex: "notes",
                            key: "notes",
                            render: (remarks: string) => {
                              if (!remarks) return "-";
                              const maxLength = 30;
                              const isLong = remarks.length > maxLength;
                              const displayText = isLong
                                ? remarks.substring(0, maxLength) + "..."
                                : remarks;
                              return (
                                <Tooltip
                                  title={isLong ? remarks : null}
                                  placement="topLeft"
                                >
                                  <div
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      cursor: isLong ? "pointer" : "default",
                                    }}
                                  >
                                    {displayText}
                                  </div>
                                </Tooltip>
                              );
                            },
                          },
                        ]}
                      />
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Current Bill Summary */}
            {totals && (
              <Card title="Current Bill Summary" className="mb-4">
                <div className="p-3 border rounded bg-white">
                  <div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Subtotal:</span>
                      <strong>₹{totals.subtotalExclTax.toLocaleString("en-IN")}</strong>
                    </div>

                    {/* GST Slab-wise Breakdown */}
                    {totals.gstSlabBreakdown &&
                      Object.keys(totals.gstSlabBreakdown).length > 0 &&
                      Object.entries(totals.gstSlabBreakdown).map(
                        ([rate, amounts]: any) => (
                          <div key={rate} className="mb-2">
                            <div className="d-flex justify-content-between mb-1">
                              <span style={{ color: "#666" }}>
                                GST @ {rate}%:
                              </span>
                              <span></span>
                            </div>
                            {amounts.igstRate > 0 ? (
                              <div
                                className="d-flex justify-content-between"
                                style={{ paddingLeft: "20px" }}
                              >
                                <span>IGST:</span>
                                <span>₹{amounts.igstRate.toLocaleString("en-IN")}</span>
                              </div>
                            ) : (
                              <>
                                <div
                                  className="d-flex justify-content-between"
                                  style={{ paddingLeft: "20px" }}
                                >
                                  <span>CGST:</span>
                                  <span>
                                    ₹{amounts.cgstRate.toLocaleString("en-IN")}
                                  </span>
                                </div>
                                <div
                                  className="d-flex justify-content-between"
                                  style={{ paddingLeft: "20px" }}
                                >
                                  <span>SGST:</span>
                                  <span>
                                    ₹{amounts.sgstRate.toLocaleString("en-IN")}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        )
                      )}

                    <div className="d-flex justify-content-between mb-2">
                      <span>
                        <strong>Gross Total:</strong>
                      </span>
                      <strong className="text-success">
                        ₹{totals.grossTotal.toLocaleString("en-IN")}
                      </strong>
                    </div>

                    {totals.tcsAmount > 0 && (
                      <div className="d-flex justify-content-between mb-2">
                        <span>
                          TCS Amount: ({totals.tcsTaxName} -{" "}
                          {totals.tcsRate.toFixed(2)}%)
                        </span>
                        <strong className="text-success">
                          ₹{totals.tcsAmount.toLocaleString("en-IN")}
                        </strong>
                      </div>
                    )}

                    {/* Mobilization Interest */}
                    {totals.interestCharged && totals.interestAmount > 0 && (
                      <div className="mb-2">
                        <div className="d-flex justify-content-between mb-1">
                          <span>
                            Mobilization Interest ({totals.interestPercentage}%
                            for {totals.interestDays} days)
                          </span>
                          <strong className="text-success">
                            +₹{totals.interestAmount.toLocaleString("en-IN")}
                          </strong>
                        </div>
                        {totals.interestFromDate && totals.interestToDate && (
                          <div
                            style={{
                              paddingLeft: "20px",
                              fontSize: "12px",
                              color: "#666",
                            }}
                          >
                            Period:{" "}
                            {dayjs(totals.interestFromDate).format("DD-MMM-YYYY")}{" "}
                            to{" "}
                            {dayjs(totals.interestToDate).format("DD-MMM-YYYY")}
                          </div>
                        )}
                      </div>
                    )}

                    {totals.tdsAmount > 0 && (
                      <div className="d-flex justify-content-between mb-2">
                        <span>
                          TDS Deduction: ({totals.tdsTaxName} -{" "}
                          {totals.tdsRate.toFixed(2)}%)
                        </span>
                        <strong className="text-danger">
                          -₹{totals.tdsAmount.toLocaleString("en-IN")}
                        </strong>
                      </div>
                    )}

                    {/* Payment Deductions Breakdown */}
                    {totals.paymentDeductions > 0 && deductions.length > 0 && (
                      <>
                        <div className="mb-1" style={{ color: "#666" }}>
                          <span>Payment Deductions:</span>
                        </div>
                        {deductions.map((deduction: any) => {
                          console.log("deductions", deduction);
                          // Calculate display percentage
                          let displayPercentage = deduction.percentValue;

                          // For Mobilization, use recoveryPercentRate
                          if (deduction.type === "Mobilization") {
                            console.log(
                              "deduction.recoveryPercentRate",
                              deduction.recoveryPercentRate
                            );
                            displayPercentage =
                              deduction.recoveryPercentRate || 0;
                          } else if (
                            !displayPercentage &&
                            deduction.calculatedAmount &&
                            totals.subtotalExclTax > 0
                          ) {
                            // Derive percentage from current bill subtotal (for fixed amounts)
                            // % = (calculatedAmount / Subtotal) × 100
                            displayPercentage =
                              (deduction.calculatedAmount /
                                totals.subtotalExclTax) *
                              100;
                          }

                          return (
                            <div
                              key={deduction.id}
                              className="d-flex justify-content-between mb-1"
                              style={{ paddingLeft: "20px" }}
                            >
                              <span>
                                {deduction.type}
                                {displayPercentage
                                  ? ` (${displayPercentage}%)`
                                  : ""}
                                :
                              </span>
                              <span className="text-danger">
                                -₹{deduction.amount.toLocaleString("en-IN")}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    )}

                    <Divider className="my-2" />
                    <div className="d-flex justify-content-between mb-2">
                      <span>
                        <strong>Current Bill Total:</strong>
                      </span>
                      <strong
                        className="text-primary"
                        style={{ fontSize: "18px" }}
                      >
                        ₹{totals.netPayable.toLocaleString("en-IN")}
                      </strong>
                    </div>

                    {/* Deductions Section */}
                    {(riskflagsItems.length > 0 || advanceItems.length > 0) && (
                      <>
                        <Divider className="my-2" />
                        <div className="mb-1" style={{ color: "#666" }}>
                          <span>
                            <strong>Deductions:</strong>
                          </span>
                        </div>

                        {/* Riskflags Items */}
                        {riskflagsItems.length > 0 && (
                          <>
                            <div
                              className="mb-1"
                              style={{ paddingLeft: "20px", color: "#666" }}
                            >
                              <span>Riskflags:</span>
                            </div>
                            {riskflagsItems.map((item: any, index: number) => (
                              <div
                                key={index}
                                className="d-flex justify-content-between mb-1"
                                style={{ paddingLeft: "40px" }}
                              >
                                <span>
                                  {item.name || `Riskflag ${index + 1}`}:
                                </span>
                                <span className="text-danger">
                                  ₹
                                  {(item.amount || 0).toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                            ))}
                          </>
                        )}

                        {/* Advance Items */}
                        {advanceItems.length > 0 && (
                          <>
                            <div
                              className="mb-1"
                              style={{ paddingLeft: "20px", color: "#666" }}
                            >
                              <span>Additional Advances:</span>
                            </div>
                            {advanceItems.map((item: any, index: number) => (
                              <div
                                key={index}
                                className="d-flex justify-content-between mb-1"
                                style={{ paddingLeft: "40px" }}
                              >
                                <span>
                                  {item.name || `Advance ${index + 1}`}:
                                </span>
                                <span className="text-danger">
                                  ₹
                                  {(item.amount || 0).toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                            ))}
                          </>
                        )}

                        {/* Total Deduction */}
                        <div className="d-flex justify-content-between mb-2 mt-2">
                          <span>
                            <strong>Total Deduction:</strong>
                          </span>
                          <strong className="text-danger">
                            -₹
                            {(
                              riskflagsItems.reduce(
                                (sum, item) => sum + (item.amount || 0),
                                0
                              ) +
                              advanceItems.reduce(
                                (sum, item) => sum + (item.amount || 0),
                                0
                              )
                            ).toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </strong>
                        </div>
                      </>
                    )}

                    {/* Additional Charges Breakdown */}
                    {additionalCharges && additionalCharges.length > 0 && (
                      <>
                        <div className="mb-1" style={{ color: "#666" }}>
                          <span>Additional Charges:</span>
                        </div>
                        {additionalCharges.map(
                          (charge: AdditionalCharge, index: number) => (
                            <div
                              key={index}
                              className="d-flex justify-content-between mb-1"
                              style={{ paddingLeft: "20px" }}
                            >
                              <span>{charge.chargeName}:</span>
                              <span className="text-info">
                                ₹{charge.amount.toLocaleString("en-IN")}
                              </span>
                            </div>
                          )
                        )}
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            <strong>Total Additional Charges:</strong>
                          </span>
                          <strong className="text-info">
                            +₹
                            {additionalCharges
                              .reduce((sum, charge) => sum + charge.amount, 0)
                              .toLocaleString("en-IN")}
                          </strong>
                        </div>
                      </>
                    )}

                    {/* Total Billed Amount - Always shown */}
                    <Divider className="my-2" />
                    <div className="d-flex justify-content-between">
                      <span>
                        <strong>Total Billed Amount:</strong>
                      </span>
                      <strong
                        className="text-success"
                        style={{ fontSize: "18px" }}
                      >
                        ₹
                        {(
                          totals.netPayable +
                          (additionalCharges && additionalCharges.length > 0
                            ? additionalCharges.reduce(
                              (sum, charge) => sum + charge.amount,
                              0
                            )
                            : 0) -
                          totals.riskFlagDeductions
                        ).toLocaleString("en-IN")}
                      </strong>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Payment Information (for approved bills) */}
            {mode === "view" && raBill.status !== "Draft" && (
              <Card title="Payment Information" className="mb-4">
                <Row gutter={[24, 16]}>
                  <Col xs={24} lg={12}>
                    <div className="border rounded p-3">
                      <div className="text-muted small">Payment Status</div>
                      <Tag
                        color={
                          raBill.paymentStatus === "Paid"
                            ? "success"
                            : raBill.paymentStatus === "Partially Paid"
                              ? "warning"
                              : "error"
                        }
                      >
                        {raBill.paymentStatus}
                      </Tag>
                    </div>
                  </Col>
                  <Col xs={24} lg={12}>
                    <div className="border rounded p-3">
                      <div className="text-muted small">Amount Paid</div>
                      <div className="fw-bold h5 mb-0 text-success">
                        {formatCurrency(raBill.paidAmount, "INR")}
                      </div>
                    </div>
                  </Col>
                  {raBill.lastPaymentDate && (
                    <>
                      <Col xs={24} lg={12}>
                        <div className="border rounded p-3">
                          <div className="text-muted small">
                            Last Payment Date
                          </div>
                          <div className="fw-bold h6 mb-0">
                            {dayjs(raBill.lastPaymentDate).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} lg={12}>
                        <div className="border rounded p-3">
                          <div className="text-muted small">Payment Mode</div>
                          <div className="fw-bold h6 mb-0">
                            {raBill.paymentMode}
                          </div>
                        </div>
                      </Col>
                    </>
                  )}
                </Row>
              </Card>
            )}

            {/* Audit Trail */}
            {/* <Card title="Audit Trail">
            <Timeline>
              <Timeline.Item
                dot={<UserOutlined className="text-primary" />}
                color="blue"
              >
                <div>
                  <strong>Created</strong> by {raBill.createdByName}
                  <div className="text-muted small">
                    {dayjs(raBill.createdAt).format("DD-MMM-YYYY HH:mm")}
                  </div>
                </div>
              </Timeline.Item>

              {raBill.submittedOn && (
                <Timeline.Item
                  dot={<CalendarOutlined className="text-warning" />}
                  color="orange"
                >
                  <div>
                    <strong>Submitted</strong> by {raBill.submittedBy}
                    <div className="text-muted small">
                      {dayjs(raBill.submittedOn).format("DD-MMM-YYYY HH:mm")}
                    </div>
                  </div>
                </Timeline.Item>
              )}

              {raBill.approvedOn && (
                <Timeline.Item
                  dot={<CheckCircleOutlined className="text-success" />}
                  color="green"
                >
                  <div>
                    <strong>Approved</strong> by {raBill.approvedBy}
                    <div className="text-muted small">
                      {dayjs(raBill.approvedOn).format("DD-MMM-YYYY HH:mm")}
                    </div>
                  </div>
                </Timeline.Item>
              )}
            </Timeline>
          </Card> */}
          </div>
        </div>

        {/* Action Modal */}
        <Modal
          title={actionType === "approve" ? "Approve RA Bill" : "Reject RA Bill"}
          open={actionModalVisible}
          onCancel={() => setActionModalVisible(false)}
          footer={null}
          width={600}
        >
          <Form form={form} onFinish={handleActionSubmit} layout="vertical">
            <div className="mb-3">
              {/* <Alert
              type={actionType === "approve" ? "success" : "warning"}
              message={
                actionType === "approve"
                  ? `You are about to approve RA Bill ${raBill.raBillNumber}`
                  : `You are about to return RA Bill ${raBill.raBillNumber} to contractor`
              }
              description={
                actionType === "approve"
                  ? "Once approved, this RA Bill will move to the next stage in the workflow."
                  : "Please provide comments explaining why this RA Bill is being returned."
              }
              showIcon
            /> */}
            </div>

            <Form.Item
              label="Comments"
              name="comments"
              rules={
                actionType === "return"
                  ? [
                    {
                      required: true,
                      message: "Comments are required when rejecting RA Bill",
                    },
                  ]
                  : []
              }
            >
              <TextArea
                rows={4}
                placeholder={`Enter your comments for ${actionType === "approve" ? "approval" : "rejection"
                  }...`}
              />
            </Form.Item>

            <div className="text-end">
              <Space>
                <Button onClick={() => setActionModalVisible(false)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  danger={actionType === "return"}
                >
                  {actionType === "approve"
                    ? "Approve RA Bill"
                    : "Reject RA Bill"}
                </Button>
              </Space>
            </div>
          </Form>
        </Modal>

        {/* Hidden PDF Generator - triggered from dropdown */}
        <div
          style={{ position: "absolute", left: "-9999px", top: 0, zIndex: -1000 }}
        >
          <RABillPdfGenerator
            raBillData={raBill}
            raBillLines={raBillLines}
            deductions={deductions}
            totals={totals}
            orgName={raBill?.contractor || "Organization"}
            triggerAttribute="data-pdf-generator-trigger"
            onPdfGenerated={() => setPdfLoading(false)}
          />
        </div>

        {/* PDF Loading Modal */}
        <Modal
          open={pdfLoading}
          footer={null}
          closable={false}
          centered
          width={300}
          afterClose={() => {
            // Restore body scroll after modal fully closes
            setTimeout(() => {
              document.body.removeAttribute("style");
            }, 50);
          }}
        >
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px", fontSize: "14px", color: "#666" }}>
              Generating PDF Preview...
            </div>
          </div>
        </Modal>
      </Content>

      <Modal
        title="Approve RA Bill"
        visible={isModalApproveVisible}
        onOk={() => {
          if (!signature.image) {
            Swal.fire({
              icon: "info",
              title: "Signature Required",
              text: "Please add your signature before approving.",
            });
            return;
          }
          openApproveConfirmSwal();
        }}

        onCancel={() => {
          setIsModalApproveVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span style={{ fontSize: 14, fontWeight: 600 }}>Signature</span>

            {signature.image && (
              <button
                type="button"
                onClick={() =>
                  setSignature({
                    isActive: true,
                    label: "Authorised Signatory",
                    image: null,
                  })
                }
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
              </button>
            )}
          </div>

          {signature.image ? (
            <>
              <div
                className="border mb-3 d-flex justify-content-center align-items-center"
                style={{
                  padding: 16,
                  borderRadius: 8,
                  background: "#fff",
                }}
              >
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <ReactstrapButton
                  size="sm"
                  color="outline-secondary"
                  onClick={() =>
                    setSignature({
                      isActive: true,
                      label: "Authorised Signatory",
                      image: null,
                    })
                  }
                >
                  Reset
                </ReactstrapButton>
              </div>
            </>
          ) : (
            <div
              style={{
                width: "100%",
                minHeight: 220,
                border: "2px dashed #dee2e6",
                borderRadius: 8,
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalApproveVisible(false)}
                  onCloseCrop={() => setIsModalApproveVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <ReactstrapButton color="primary" size="sm" outline style={{ minWidth: 140 }} onClick={() => setActiveSignatureContext("APPROVE")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </ReactstrapButton>
                </ImageUploadCropper>

                <ReactstrapButton
                  color="outline-primary"
                  size="sm"
                  style={{ minWidth: 140 }}
                  onClick={() => {
                    setIsModalApproveVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </ReactstrapButton>
              </div>
            </div>
          )}
        </div>
      </Modal >

      <Modal
        title="Reject RA Bill"
        visible={isModalRejectedVisible}
        onOk={openRejectConfirmSwal}
        onCancel={() => {
          handleCancel();
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea rows={3} placeholder="Type your reason here..." />
          </Form.Item>

          <div className="mt-4">
            <strong style={{ fontSize: "16px" }}>Signature</strong>
          </div>

          {signature.image && (
            <a
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() =>
                setSignature({ isActive: true, label: "Authorised Signatory", image: null })
              }
            >
            </a>
          )}


          {signature.image ? (
            <>
              <div className="signatueImgWrapper border mb-3 p-3 text-center">
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ width: "180px", height: "auto" }}
                />
              </div>
              <div className="d-flex justify-content-end mb-3">
                <ReactstrapButton
                  size="sm"
                  color="outline-secondary"
                  className="me-2"
                  onClick={() =>
                    setSignature({ isActive: true, label: "Authorised Signatory", image: null })
                  }
                >
                  Reset
                </ReactstrapButton>
              </div>

            </>
          ) : (
            <div
              style={{
                minHeight: "200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "15px",
                border: "2px dashed #dee2e6",
                borderRadius: "8px",
                backgroundColor: "#f8f9fa"
              }}
            >
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalRejectedVisible(false)}
                  onCloseCrop={() => setIsModalRejectedVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <ReactstrapButton color="primary" size="sm" outline onClick={() => setActiveSignatureContext("REJECT")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </ReactstrapButton>
                </ImageUploadCropper>

                <ReactstrapButton
                  color="outline-primary"
                  size="sm"
                  onClick={() => {
                    setIsModalRejectedVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </ReactstrapButton>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {showSignatureModel && (
        <SignatureModal
          modal={showSignatureModel}
          toggle={() => {
            setShowSignatureModel(false);
            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
          onSave={(data: string) => {
            setSignature((prev: any) => ({ ...prev, image: data }));
            setShowSignatureModel(false);

            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
        />
      )}
    </>
  );
};

export default RABillDetails;